import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, NgZone, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@env/environment';
import { ToasterService } from '@lib/components/toaster/toaster.service';
import {
    ForgotPasswordRequest,
    LoginRequest,
    LoginResponse,
    RefreshTokenRequest,
    ResetPasswordRequest,
    SignupRequest,
    SignupResponse,
} from '@lib/interfaces/auth.interface';
import { clearAppSession, selectAppSession, setAppSession } from '@lib/store';
import { isValidSession, shouldRefreshToken } from '@lib/utils/auth/auth.utils';
import { Store } from '@ngrx/store';
import { CredentialResponse } from 'google-one-tap';
import { BehaviorSubject, catchError, interval, Observable, startWith, switchMap, take, takeWhile, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    isAuthenticated$ = new BehaviorSubject<boolean>(false);
    currentUserRoles$ = new BehaviorSubject<string[]>([]);
    currentUserInitials$ = new BehaviorSubject<string>('U');
    currentUserId$ = new BehaviorSubject<string>('');
    returnUrl = signal<string>('');
    private readonly _router = inject(Router);
    private readonly _route = inject(ActivatedRoute);
    private readonly _ngZone = inject(NgZone);
    private readonly _http = inject(HttpClient);
    private readonly _toaster = inject(ToasterService);
    private readonly _store = inject(Store);

    constructor() {
        this._store.select(selectAppSession).subscribe((session) => {
            this.isAuthenticated$.next(!!session && isValidSession(session));
        });

        this._route.queryParams.subscribe((params) => {
            if (params['returnUrl']) {
                this.returnUrl.set(params['returnUrl']);
            }
        });

        this.startTokenRefreshMonitor();
    }

    get isAuthenticated(): boolean {
        return this.isAuthenticated$.getValue();
    }

    get currentUserRoles(): string[] {
        return this.currentUserRoles$.getValue();
    }

    get currentUserInitials(): string {
        return this.currentUserInitials$.getValue();
    }

    get currentUserId(): string {
        return this.currentUserId$.getValue();
    }

    loginWithGoogle = (response: CredentialResponse) => {
        try {
            const token = response.credential;
            if (!token) return;

            const payload = {
                authProvider: 'google',
                token,
            };

            this._http.post(`${environment.API_URL}/auth/oauth/login`, payload).subscribe({
                next: (res: any) => {
                    this._store.dispatch(setAppSession({ session: { ...res.data } }));
                    this.currentUserRoles$.next(res.data.roleList);
                    this.isAuthenticated$.next(true);

                    // Navigate within Angular zone
                    this._ngZone.run(() => {
                        this._router.navigate([this.returnUrl() || '/courses']);
                    });
                },
                error: (err) => {
                    this._toaster.show({
                        message: 'Google sign-in failed. Please try again.',
                        status: 'error',
                    });
                    console.warn('OAuth login failed:', err);
                },
            });
        } catch (error) {
            this._toaster.show({
                message: 'An unexpected error occurred during sign-in. Please try again.',
                status: 'error',
            });
            console.warn('Unexpected error during Google login:', error);
        }
    };

    logout(): void {
        this._store.dispatch(clearAppSession());
        this.isAuthenticated$.next(false);
    }

    signinWithEmail(credentials: LoginRequest): Observable<LoginResponse> {
        return this._http.post<LoginResponse>(`${environment.API_URL}/auth/login`, credentials).pipe(
            tap((response) => {
                this._store.dispatch(setAppSession({ session: { ...response.data } }));
                this.initializeSession(response.data);
                this.currentUserRoles$.next(response.data.roleList);
                this.isAuthenticated$.next(true);
            }),
            catchError((error) => {
                // Let the component handle specific error messages from server
                // Only show generic message if no specific error message
                if (!error.error?.message) {
                    this._toaster.show({
                        message: 'Login failed. Please check your credentials and try again.',
                        status: 'error',
                    });
                }
                console.warn('Login failed:', error);
                throw error;
            }),
        );
    }

    signupWithEmail(request: SignupRequest): Observable<SignupResponse> {
        return this._http.post<SignupResponse>(`${environment.API_URL}/auth/student`, request);
    }

    checkIfEmailExists(email: string): Observable<SignupResponse> {
        const params = new HttpParams().set('email', email);
        return this._http.get<SignupResponse>(`${environment.API_URL}/auth/email`, { params });
    }

    forgotPassword(request: ForgotPasswordRequest): Observable<any> {
        return this._http.post(`${environment.API_URL}/auth/forgot-password`, request);
    }

    resetPassword(request: ResetPasswordRequest): Observable<any> {
        return this._http.post(`${environment.API_URL}/auth/set-password`, request);
    }

    /**
     * Calls the refresh token endpoint and returns the response.
     * @param request The refresh token request object containing Authorization and refreshToken
     */
    refreshToken(request: RefreshTokenRequest): Observable<any> {
        const params = new HttpParams().set('refreshToken', request.refreshToken);
        return this._http.post<any>(
            `${environment.API_URL}/auth/refresh-token`,
            {},
            {
                headers: { Authorization: request.Authorization },
                params,
            },
        );
    }

    logoutUser(): Observable<any> {
        return this._http.post(`${environment.API_URL}/auth/logout`, {}).pipe(
            tap({
                next: () => {
                    this.logout();
                },
                error: (err) => {
                    this._toaster.show({ message: 'Logout failed. Please try again.', status: 'error' });
                },
            }),
        );
    }

    initializeSession(appSession: any) {
        if (!appSession) return;

        const { roleList, initials: userInitials, userId } = appSession;

        if (roleList && JSON.stringify(roleList) !== JSON.stringify(this.currentUserRoles)) {
            this.currentUserRoles$.next(roleList);
        }

        if (userInitials && userInitials !== this.currentUserInitials) {
            this.currentUserInitials$.next(userInitials);
        }

        if (userId && userId !== this.currentUserId) {
            this.currentUserId$.next(userId);
        }
    }

    private startTokenRefreshMonitor(): void {
        // Check every 5 minutes for token refresh needs
        interval(5 * 60 * 1000)
            .pipe(
                startWith(0),
                switchMap(() => this._store.select(selectAppSession)),
                takeWhile(() => true, true),
            )
            .subscribe((session) => {
                if (session && shouldRefreshToken(session)) {
                    this.proactiveTokenRefresh(session);
                }
            });
    }

    private proactiveTokenRefresh(session: any): void {
        const refreshRequest: RefreshTokenRequest = {
            Authorization: `Bearer ${session.accessToken}`,
            refreshToken: session.refreshToken,
        };

        this.refreshToken(refreshRequest).subscribe({
            next: (response) => {
                this._store.dispatch(setAppSession({ session: { ...session, ...response.data } }));
                this.initializeSession(response.data);
            },
            error: (error) => {
                this._toaster.show({ message: error.error.message, status: 'error' });
            },
        });
    }

    validateCurrentSession(): boolean {
        let isValid = false;
        this._store
            .select(selectAppSession)
            .pipe(take(1))
            .subscribe((session) => {
                if (session && !isValidSession(session)) {
                    this.logout();
                    isValid = false;
                } else {
                    isValid = !!session;
                }
            });
        return isValid;
    }
}
